import React, { useState } from "react";
import { colors } from "../styles/colors";
import { FaInstagram, FaWhatsapp,FaLinkedinIn, FaBars, FaTimes } from "react-icons/fa";
import styled from "styled-components";
import { Link } from "react-scroll";
import LogoImg from "../assets/images/ce5a9991-ed96-4356-aee7-7c4bfbd9d26a.png";

const HeaderWrapper = styled.header`
  display: flex;
  justify-content: space-between;
  padding: 15px 80px;
  align-items: center;
  background-color: ${colors.background};
  position: sticky;
  top: 0;
  z-index: 100;
  border-bottom: 1px solid rgba(15, 59, 46, 0.1);
  
  @media (max-width: 1024px) {
    padding: 15px 40px;
  }

  @media (max-width: 768px) {
    padding: 10px 20px;
  }
`;

const Logo = styled.img`
 height: 85px;                /* Increased height from 60px to 85px to make it prominent */
  margin-top: -10px;           /* Pulls the top up slightly so the header bar doesn't grow taller */
  margin-bottom: -10px;        /* Pulls the bottom up slightly to keep layout uniform */
  object-fit: contain;
  cursor: pointer;               /* Stay above mobile overlay background elements */
  
  @media (max-width: 768px) {
    height: 75px;              /* Scaled safely down on mobile viewpoints */
    margin-top: -10px;
    margin-bottom: -10px;
  }
`;

// Desktop navigation link list container
const Nav = styled.nav`
  display: flex;
  gap: 35px;
  align-items: center;

  @media (max-width: 768px) {
    display: none;             /* Hides native flat links layout on mobile */
  }

  a {
    font-family: 'Montserrat', sans-serif;
    font-weight: 500;
    font-size: 0.9rem;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: ${colors.secondary};
    cursor: pointer;
    transition: color 0.3s ease;
    
    &:hover {
      color: ${colors.primary};
    }
  }
`;

const SocialIcons = styled.div`
  display: flex;
  gap: 20px;
  margin-left: 15px;
  border-left: 1px solid rgba(15, 59, 46, 0.2);
  padding-left: 20px;

  a {
    font-size: 1.2rem;
    color: ${colors.secondary};
    transition: transform 0.3s ease, color 0.3s ease;

    &:hover {
      color: ${colors.primary};
      transform: translateY(-2px);
    }
  }
`;

// Hamburger toggle button layout wrapper
const MenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: ${colors.secondary};
  font-size: 1.5rem;
  cursor: pointer;
  z-index: 102;
  transition: color 0.3s ease;

  &:hover {
    color: ${colors.primary};
  }

  @media (max-width: 768px) {
    display: block;            /* Makes menu button visible solely on phone screens */
  }
`;

// Responsive full-viewport drawer overlay container
const MobileMenuOverlay = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background-color: ${colors.background || "#ffffff"};
  z-index: 101;
  transition: transform 0.4s cubic-bezier(0.77, 0.2, 0.05, 1);
  transform: ${(props) => (props.isOpen ? "translateX(0)" : "translateX(100%)")};

  /* Navigation Link Layout inside drawer */
  .mobile-nav-links {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 30px;
    margin-bottom: 40px;

    a {
      font-family: 'Montserrat', sans-serif;
      font-size: 1.2rem;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.1em;
      color: ${colors.secondary};
      text-decoration: none;
      cursor: pointer;
      transition: color 0.3s ease;

      &:hover {
        color: ${colors.primary};
      }
    }
  }

  /* Social Icons layout adjustments inside drawer bottom frame */
  .mobile-socials {
    display: flex;
    gap: 25px;
    
    a {
      font-size: 1.6rem;
      color: ${colors.secondary};
      transition: color 0.3s ease;

      &:hover {
        color: ${colors.primary};
      }
    }
  }
`;

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  return (
    <HeaderWrapper>
      <Logo src={LogoImg} alt="Saleha Safdar Global Consulting Logo" />
      
      {/* Desktop Layout Structure */}
      <Nav>
        <Link to="about" smooth={true} duration={800} offset={-80}>About</Link>
        <Link to="services" smooth={true} duration={800} offset={-80}>Services</Link>
        <Link to="video" smooth={true} duration={800} offset={-80}>Meet</Link>
        <Link to="team" smooth={true} duration={800} offset={-80}>Team</Link>
        <Link to="contact" smooth={true} duration={800} offset={-80}>+971509133007</Link>
        <SocialIcons>
          <a href="https://wa.me/03330615143" target="_blank" rel="noreferrer">
            <FaWhatsapp />
          </a>
          <a href="https://www.instagram.com/salehainfocus" target="_blank" rel="noreferrer">
            <FaInstagram />
          </a>
           <a href="https://www.linkedin.com/company/127994152/admin/dashboard/" target="_blank" rel="noreferrer">
            <FaLinkedinIn />
          </a>
        </SocialIcons>
      </Nav>

      {/* Hamburger Trigger for Small Devices */}
      <MenuButton onClick={toggleMenu} aria-label="Toggle Navigation Menu">
        {isMenuOpen ? <FaTimes /> : <FaBars />}
      </MenuButton>

      {/* Slide-out Overlay Drawer Layout for Small Devices */}
      <MobileMenuOverlay isOpen={isMenuOpen}>
        <div className="mobile-nav-links">
          <Link to="about" smooth={true} duration={800} offset={-70} onClick={closeMenu}>About</Link>
          <Link to="services" smooth={true} duration={800} offset={-70} onClick={closeMenu}>Services</Link>
          <Link to="video" smooth={true} duration={800} offset={-70} onClick={closeMenu}>Meet</Link>
          <Link to="team" smooth={true} duration={800} offset={-70} onClick={closeMenu}>Team</Link>
          <Link to="contact" smooth={true} duration={800} offset={-70} onClick={closeMenu}>+971509133007</Link>
        </div>
        <div className="mobile-socials">
          <a href="https://wa.me/03330615143" target="_blank" rel="noreferrer" onClick={closeMenu}>
            <FaWhatsapp />
          </a>
          <a href="https://www.instagram.com/salehainfocus" target="_blank" rel="noreferrer" onClick={closeMenu}>
            <FaInstagram />
          </a>
          <a href="https://www.linkedin.com/company/127994152/admin/dashboard/" target="_blank" rel="noreferrer" onClick={closeMenu}>
  <FaLinkedinIn />
</a>
        </div>
      </MobileMenuOverlay>
    </HeaderWrapper>
  );
}